import React, {useState, useMemo, useCallback, useRef, useEffect, type ReactNode} from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {Link as RouterLink} from 'react-router-dom';
import {useMutation, useApolloClient, useQuery} from '@apollo/client';
import {DELETE_SOURCE_ITEMS, COMMIT_UPLOADS_TO_SOURCE_ITEMS, EXTRACT_SOURCE_ITEMS, CLASSIFY_SOURCE_ITEMS} from '../graphql/mutations';
import {GET_SOURCE_ITEMS_EXPORT, GET_EVAL_TEST_CASES} from '../graphql/queries';
import {useAuth} from '../contexts/AuthContext';
import {useOrg} from '../contexts/OrgContext';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useFileUpload, ACCEPT_STRING} from '../hooks/useFileUpload';
import {colors} from '../theme/colors';
import AddToGroupDialog from '../components/upload-dropzone/AddToGroupDialog';
import SourceItemDrawer from '../components/SourceItemDrawer';
import ScanLibraryDialog from '../components/source-library/ScanLibraryDialog';
import WebExplorerTree from '../components/source-library/WebExplorerTree';
import SourceItemList from '../components/source-library/SourceItemList';
import SourceItemFilterBar, {type SourceItemFilterState} from '../components/source-library/SourceItemFilterBar';

const TREE_WIDTH_KEY = 'planapus:sourceLibrary:treeWidth';
const DEFAULT_TREE_WIDTH = 340;
const MIN_TREE_WIDTH = 260;
const MAX_TREE_WIDTH = 520;

function formatFileSize(bytes?: number): string {
  if (!bytes) return '\u2014';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function humanizeSegment(segment: string): string {
  try {
    segment = decodeURIComponent(segment);
  } catch { /* ignore */ }
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function parseBreadcrumb(nodeId: string): { segments: { label: string; muted?: boolean }[]; secondary?: string } | null {
  const colonIdx = nodeId.indexOf(':');
  if (colonIdx === -1) return null;
  const kind = nodeId.substring(0, colonIdx);
  const value = nodeId.substring(colonIdx + 1);

  if (kind === 'uncategorized') {
    return { segments: [{ label: 'Uncategorized' }] };
  }

  if (kind === 'domain') {
    return { segments: [{ label: value }] };
  }

  if (kind === 'prefix') {
    // value is "domain:/path/prefix"
    const domainColonIdx = value.indexOf(':');
    const domain = value.substring(0, domainColonIdx);
    const prefixPath = value.substring(domainColonIdx + 1);
    const pathSegments = prefixPath.split('/').filter(Boolean);
    const segments = [
      { label: domain },
      ...pathSegments.map(s => ({ label: humanizeSegment(s) })),
    ];
    return { segments, secondary: prefixPath };
  }

  if (kind === 'page') {
    // value is full URL
    try {
      const parsed = new URL(value);
      const domain = parsed.hostname.replace(/^www\./, '');
      const pathSegments = parsed.pathname.split('/').filter(Boolean);
      const segments = [
        { label: domain },
        ...pathSegments.map(s => ({ label: humanizeSegment(s) })),
      ];
      return { segments, secondary: value };
    } catch {
      return { segments: [{ label: value }] };
    }
  }

  return null;
}

export type GroupBy = 'none' | 'source_page';

export default function SourceLibraryPage() {
  useDocumentTitle('Source Library');

  const {user} = useAuth();
  const {activeOrg} = useOrg();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetIds, setDeleteTargetIds] = useState<string[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'; action?: ReactNode}>({
    open: false, message: '', severity: 'info',
  });

  // Eval test case IDs — used to show badge on flagged items
  const {data: evalCasesData} = useQuery(GET_EVAL_TEST_CASES);
  const evalCaseIds = useMemo(() => {
    const ids = new Set<string>();
    for (const tc of evalCasesData?.evalTestCases || []) ids.add(tc.sourceItemId);
    return ids;
  }, [evalCasesData]);

  // Tree navigation state
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Resizable pane width
  const [treeWidth, setTreeWidth] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(TREE_WIDTH_KEY);
      if (stored) {
        const val = parseInt(stored, 10);
        if (val >= MIN_TREE_WIDTH && val <= MAX_TREE_WIDTH) return val;
      }
    } catch { /* ignore */ }
    return DEFAULT_TREE_WIDTH;
  });
  const draggingRef = useRef(false);

  const handleResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = treeWidth;

    const onPointerMove = (ev: PointerEvent) => {
      const delta = ev.clientX - startX;
      const newWidth = Math.min(MAX_TREE_WIDTH, Math.max(MIN_TREE_WIDTH, startWidth + delta));
      setTreeWidth(newWidth);
    };

    const onPointerUp = () => {
      draggingRef.current = false;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      setTreeWidth(w => {
        try { localStorage.setItem(TREE_WIDTH_KEY, String(w)); } catch { /* ignore */ }
        return w;
      });
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
  }, [treeWidth]);

  // Group-by state
  const [groupBy, setGroupBy] = useState<GroupBy>('none');

  // Filter state (combines tree selection + filter bar)
  const [filterBarState, setFilterBarState] = useState<SourceItemFilterState>({});

  const combinedFilter = useMemo<SourceItemFilterState>(() => ({
    ...filterBarState,
    nodeId: selectedNodeId,
  }), [filterBarState, selectedNodeId]);

  const {
    attachments,
    addFiles,
    removeAttachment,
    retryFile,
    clearAll,
    hasUploading,
    readyTempUploadIds,
  } = useFileUpload({maxFiles: 30});

  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [libraryTotalCount, setLibraryTotalCount] = useState(0);

  const [commitUploads] = useMutation(COMMIT_UPLOADS_TO_SOURCE_ITEMS);
  const [deleteSourceItems] = useMutation(DELETE_SOURCE_ITEMS);
  const [extractSourceItems] = useMutation(EXTRACT_SOURCE_ITEMS);
  const [classifySourceItems] = useMutation(CLASSIFY_SOURCE_ITEMS);

  const apolloClient = useApolloClient();
  const [exporting, setExporting] = useState(false);

  const handleExportMarkdown = useCallback(async () => {
    if (!activeOrg?.id || exporting) return;
    setExporting(true);
    try {
      // Fetch all items in pages
      const allItems: any[] = [];
      let after: string | null = null;
      let hasMore = true;
      while (hasMore) {
        const result = await apolloClient.query({
          query: GET_SOURCE_ITEMS_EXPORT,
          variables: {orgId: activeOrg.id, first: 200, after},
          fetchPolicy: 'no-cache',
        });
        const data: any = result.data;
        const edges = data?.sourceItemsConnection?.edges ?? [];
        for (const edge of edges) {
          allItems.push(edge.node);
        }
        hasMore = data?.sourceItemsConnection?.pageInfo?.hasNextPage ?? false;
        after = data?.sourceItemsConnection?.pageInfo?.endCursor ?? null;
      }

      // Build markdown
      const lines: string[] = [];
      lines.push(`# Source Library Export`);
      const orgLine = activeOrg.name + (activeOrg.website ? ` (${activeOrg.website})` : '');
      lines.push(`\n**Organization:** ${orgLine}  `);
      lines.push(`**Exported:** ${new Date().toISOString()}  `);
      lines.push(`**Total documents:** ${allItems.length}`);
      lines.push('');
      lines.push('---');
      lines.push('');

      for (const item of allItems) {
        lines.push(`## ${item.title}`);
        lines.push('');

        // Metadata line
        const meta: string[] = [];
        meta.push(`**Type:** ${item.type}`);
        if (item.sourceUrl) meta.push(`**URL:** ${item.sourceUrl}`);
        meta.push(`**Extraction:** ${item.extractionStatus}`);
        lines.push(meta.join(' | '));
        lines.push('');

        // Classification
        const override = item.classificationOverride;
        const wave2 = item.classificationWave2;
        const wave1 = item.classificationWave1;
        const effective = override ?? wave2 ?? wave1;
        if (effective) {
          const isProcess = override ? override.isProcess : effective.is_process;
          const refsProcess = !override && effective.references_process;
          const classItems: string[] = [];
          classItems.push(`**Classification:** ${isProcess ? 'Process' : 'Not a process'}`);
          if (refsProcess) classItems.push('**References process:** Yes');
          if (effective.doc_type_hint) classItems.push(`**Doc type:** ${effective.doc_type_hint}`);
          if (effective.confidence !== undefined) classItems.push(`**Confidence:** ${(effective.confidence * 100).toFixed(0)}%`);
          lines.push(classItems.join(' | '));
          lines.push('');
        } else if (item.classificationStatus) {
          lines.push(`**Classification:** ${item.classificationStatus}`);
          lines.push('');
        }

        // Extraction metadata (summary, topics, etc.)
        const em = item.extractionMetadata;
        if (em) {
          if (em.summary) {
            lines.push(`**Summary:** ${em.summary}`);
            lines.push('');
          }
          if (em.documentType) {
            lines.push(`**Document type:** ${em.documentType}`);
            lines.push('');
          }
          if (em.audience) {
            lines.push(`**Audience:** ${em.audience}`);
            lines.push('');
          }
          if (em.topics?.length) {
            lines.push(`**Topics:** ${em.topics.join(', ')}`);
            lines.push('');
          }
          if (em.entities?.length) {
            lines.push(`**Entities:** ${em.entities.join(', ')}`);
            lines.push('');
          }
        }

        // Linked pages
        if (item.linkedFromPages?.length) {
          lines.push('**Discovered on:**');
          for (const page of item.linkedFromPages) {
            lines.push(`- ${page.title ? `${page.title} — ` : ''}${page.url}`);
          }
          lines.push('');
        }

        lines.push('---');
        lines.push('');
      }

      // Download
      const blob = new Blob([lines.join('\n')], {type: 'text/markdown'});
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `source-library-export-${new Date().toISOString().slice(0, 10)}.md`;
      a.click();
      URL.revokeObjectURL(url);

      setSnackbar({open: true, severity: 'success', message: `Exported ${allItems.length} documents`});
    } catch (err: any) {
      setSnackbar({open: true, severity: 'error', message: `Export failed: ${err.message}`});
    } finally {
      setExporting(false);
    }
  }, [activeOrg?.id, exporting, apolloClient]);

  // Auto-commit files once all uploads finish
  const committingRef = useRef(false);
  useEffect(() => {
    if (!hasUploading && readyTempUploadIds.length > 0 && activeOrg?.id && !committingRef.current) {
      committingRef.current = true;
      commitUploads({variables: {orgId: activeOrg.id, tempUploadIds: readyTempUploadIds}})
        .then((result) => {
          const count = result.data?.commitUploadsToSourceItems?.length ?? 0;
          setSnackbar({open: true, severity: 'success', message: `Added ${count} document${count === 1 ? '' : 's'} to library`});
          clearAll();
        })
        .catch((err: any) => {
          setSnackbar({open: true, severity: 'error', message: err.message});
        })
        .finally(() => {
          committingRef.current = false;
        });
    }
  }, [hasUploading, readyTempUploadIds, activeOrg?.id, commitUploads, clearAll]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  }, [addFiles]);

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDeleteClick = useCallback((ids: string[]) => {
    setDeleteTargetIds(ids);
    setDeleteConfirmOpen(true);
  }, []);

  const handleSelectItem = useCallback((item: any) => {
    setSelectedItemId(item.id);
  }, []);

  const handleSnackbar = useCallback((msg: {open: boolean; message: string; severity: 'success' | 'error' | 'info'; action?: ReactNode}) => {
    setSnackbar(msg);
  }, []);

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const {data: result} = await deleteSourceItems({variables: {ids: deleteTargetIds}});
      const deleted = result?.deleteSourceItems?.deletedIds?.length ?? 0;
      const blocked = result?.deleteSourceItems?.blockedItems ?? [];

      if (blocked.length > 0 && deleted > 0) {
        const reasons = blocked.map((b: any) => `"${b.title}": ${b.reason}`).join('\n');
        setSnackbar({open: true, severity: 'info', message: `Deleted ${deleted} item(s). ${blocked.length} blocked:\n${reasons}`});
      } else if (blocked.length > 0) {
        const reasons = blocked.map((b: any) => `"${b.title}": ${b.reason}`).join('; ');
        setSnackbar({open: true, severity: 'error', message: `Cannot delete: ${reasons}`});
      } else {
        setSnackbar({open: true, severity: 'success', message: `Deleted ${deleted} item(s)`});
      }

      setSelectedIds(prev => {
        const next = new Set(prev);
        (result?.deleteSourceItems?.deletedIds ?? []).forEach((id: string) => next.delete(id));
        return next;
      });
    } catch (err: any) {
      setSnackbar({open: true, severity: 'error', message: err.message});
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteTargetIds([]);
    }
  };

  const breadcrumb = useMemo(() => {
    if (!selectedNodeId) return null;
    return parseBreadcrumb(selectedNodeId);
  }, [selectedNodeId]);

  if (!user || !activeOrg?.id) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column'}}>
      {/* Header */}
      <Box sx={{px: 3, pt: 3, pb: 1.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Box>
            <Typography sx={{fontSize: 24, fontWeight: 650, color: colors.textPrimary}}>
              Source Library
            </Typography>
            <Typography sx={{fontSize: 14, color: '#6B7280', mt: 0.5}}>
              Upload and manage source documents for your guides
            </Typography>
          </Box>
          <Box sx={{display: 'flex', gap: 1}}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportMarkdown}
              disabled={exporting}
              sx={{textTransform: 'none'}}
            >
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setScanDialogOpen(true)}
              sx={{textTransform: 'none'}}
            >
              Scan Library
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 2-pane layout */}
      <Box sx={{flex: 1, display: 'flex', minHeight: 0}}>
        {/* Left pane: tree */}
        <Box sx={{width: treeWidth, flexShrink: 0, borderRight: '1px solid #E5E7EB', bgcolor: '#FAFAFA'}}>
          <WebExplorerTree
            orgId={activeOrg.id}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
          />
        </Box>

        {/* Resize handle */}
        <Box
          onPointerDown={handleResizePointerDown}
          sx={{
            width: 4,
            flexShrink: 0,
            cursor: 'col-resize',
            bgcolor: 'transparent',
            '&:hover': {bgcolor: colors.blue[200]},
            transition: 'background-color 0.15s',
          }}
        />

        {/* Right pane */}
        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0}}>
          {/* Upload zone + queue */}
          <Box sx={{px: 2, pt: 2}}>
            {/* Drop zone */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragOver ? colors.blue[400] : '#D1D5DB'}`,
                borderRadius: '12px',
                bgcolor: dragOver ? colors.blue[50] : '#FAFAFA',
                py: 2,
                px: 3,
                mb: 1.5,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: colors.blue[300],
                  bgcolor: colors.blue[50],
                },
              }}
            >
              <CloudUploadIcon sx={{fontSize: 28, color: dragOver ? colors.blue[500] : '#9CA3AF', mb: 0.5}} />
              <Typography sx={{fontSize: 13, fontWeight: 500}}>
                Drag & drop files here, or click to browse
              </Typography>
              <Typography sx={{fontSize: 11, color: '#9CA3AF', mt: 0.25}}>
                PDF, DOCX, PNG, JPG, MD, TXT
              </Typography>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_STRING}
              style={{display: 'none'}}
              onChange={handleFileSelect}
            />

            {/* Upload queue */}
            {attachments.length > 0 && (
              <Box
                sx={{
                  mb: 1.5,
                  bgcolor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                {attachments.map((att) => (
                  <Box
                    key={att.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 2,
                      borderBottom: '1px solid #F3F4F6',
                      '&:last-child': {borderBottom: 'none'},
                    }}
                  >
                    <Box sx={{flexShrink: 0, width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      {att.status === 'uploading' && <CircularProgress size={16} />}
                      {att.status === 'ready' && <CheckCircleIcon sx={{fontSize: 18, color: colors.green[600]}} />}
                      {att.status === 'failed' && <ErrorIcon sx={{fontSize: 18, color: colors.red[600]}} />}
                    </Box>
                    <Box sx={{flex: 1, minWidth: 0}}>
                      <Typography sx={{fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                        {att.fileName}
                      </Typography>
                      {att.status === 'uploading' && (
                        <LinearProgress variant="determinate" value={att.progress} sx={{mt: 0.5, height: 3, borderRadius: 2}} />
                      )}
                      {att.status === 'failed' && att.errorMessage && (
                        <Typography sx={{fontSize: 11, color: colors.red[600]}}>{att.errorMessage}</Typography>
                      )}
                    </Box>
                    <Typography sx={{fontSize: 12, color: '#9CA3AF', flexShrink: 0}}>
                      {formatFileSize(att.sizeBytes)}
                    </Typography>
                    {att.status === 'failed' && (
                      <IconButton size="small" onClick={() => retryFile(att.id)} title="Retry">
                        <ReplayIcon sx={{fontSize: 16}} />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => removeAttachment(att.id)} title="Remove">
                      <DeleteOutlineIcon sx={{fontSize: 16, color: '#9CA3AF'}} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Selection action bar */}
            {selectedIds.size > 0 && (
              <Box
                sx={{
                  mb: 1.5,
                  px: 2.5,
                  py: 1.5,
                  bgcolor: colors.blue[600],
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{fontSize: 14, fontWeight: 500, color: '#fff'}}>
                  {selectedIds.size} selected
                </Typography>
                <Box sx={{display: 'flex', gap: 1}}>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{bgcolor: '#fff', color: colors.blue[600], textTransform: 'none', '&:hover': {bgcolor: colors.gray[100]}}}
                    onClick={() => setAddToGroupOpen(true)}
                  >
                    Add to group
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{bgcolor: '#fff', color: colors.blue[600], textTransform: 'none', '&:hover': {bgcolor: colors.gray[100]}}}
                    onClick={async () => {
                      try {
                        const result = await extractSourceItems({variables: {orgId: activeOrg?.id, sourceItemIds: Array.from(selectedIds)}});
                        const jobId = result.data?.extractSourceItems?.id;
                        setSnackbar({
                          open: true, severity: 'success', message: `Extraction started for ${selectedIds.size} item(s)`,
                          action: jobId ? <Button color="inherit" size="small" component={RouterLink} to={`/org/jobs/${jobId}`} sx={{textTransform: 'none', fontWeight: 600}}>View job</Button> : undefined,
                        });
                        setSelectedIds(new Set());
                      } catch (err: any) {
                        setSnackbar({open: true, severity: 'error', message: err.message});
                      }
                    }}
                  >
                    Extract
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{bgcolor: '#fff', color: colors.blue[600], textTransform: 'none', '&:hover': {bgcolor: colors.gray[100]}}}
                    onClick={async () => {
                      try {
                        const result = await classifySourceItems({variables: {orgId: activeOrg?.id, sourceItemIds: Array.from(selectedIds)}});
                        const jobId = result.data?.classifySourceItems?.id;
                        setSnackbar({
                          open: true, severity: 'success', message: `Classification started for ${selectedIds.size} item(s)`,
                          action: jobId ? <Button color="inherit" size="small" component={RouterLink} to={`/org/jobs/${jobId}`} sx={{textTransform: 'none', fontWeight: 600}}>View job</Button> : undefined,
                        });
                        setSelectedIds(new Set());
                      } catch (err: any) {
                        setSnackbar({open: true, severity: 'error', message: err.message});
                      }
                    }}
                  >
                    Classify
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DeleteOutlineIcon sx={{fontSize: 16}} />}
                    sx={{bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', textTransform: 'none', '&:hover': {bgcolor: 'rgba(255,255,255,0.25)'}}}
                    onClick={() => handleDeleteClick(Array.from(selectedIds))}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            )}

            {/* Breadcrumb scope header */}
            {breadcrumb && (
              <Box sx={{display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.5}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap'}}>
                    {breadcrumb.segments.map((seg, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && (
                          <Typography sx={{fontSize: 13, color: '#9CA3AF', flexShrink: 0}}>›</Typography>
                        )}
                        <Typography sx={{
                          fontSize: 13,
                          fontWeight: i === breadcrumb.segments.length - 1 ? 600 : 400,
                          color: i === breadcrumb.segments.length - 1 ? colors.textPrimary : '#6B7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {seg.label}
                        </Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                  {breadcrumb.secondary && (
                    <Typography sx={{fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      {breadcrumb.secondary}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => setSelectedNodeId(null)} title="Clear scope">
                  <CloseIcon sx={{fontSize: 16, color: '#9CA3AF'}} />
                </IconButton>
              </Box>
            )}

            {/* Filter bar */}
            <SourceItemFilterBar
              filter={filterBarState}
              onFilterChange={setFilterBarState}
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
            />
          </Box>

          {/* Item list */}
          <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <SourceItemList
              orgId={activeOrg.id}
              filter={combinedFilter}
              selectedIds={selectedIds}
              onToggleSelect={handleToggle}
              onItemClick={handleSelectItem}
              onDeleteClick={handleDeleteClick}
              groupBy={groupBy}
              evalCaseIds={evalCaseIds}
              onTotalCountChange={setLibraryTotalCount}
            />
          </Box>
        </Box>
      </Box>

      {/* Scan Library dialog */}
      <ScanLibraryDialog
        open={scanDialogOpen}
        onClose={() => setScanDialogOpen(false)}
        orgId={activeOrg?.id}
        totalItems={libraryTotalCount}
        onSnackbar={handleSnackbar}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setDeleteTargetIds([]); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>
          Delete {deleteTargetIds.length === 1 ? 'source item' : `${deleteTargetIds.length} source items`}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 14, color: '#6B7280'}}>
            {deleteTargetIds.length === 1
              ? 'This item will be permanently deleted. If it is used by any groups, citations, or suggestions, deletion will be blocked.'
              : 'Items without dependencies will be deleted. Items still in use by groups, citations, or suggestions will be skipped and reported.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2}}>
          <Button
            onClick={() => { setDeleteConfirmOpen(false); setDeleteTargetIds([]); }}
            sx={{textTransform: 'none'}}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            sx={{textTransform: 'none'}}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to group dialog (from selection) */}
      {addToGroupOpen && (
        <AddToGroupDialog
          open={addToGroupOpen}
          onClose={() => {
            setAddToGroupOpen(false);
            setSelectedIds(new Set());
          }}
          sourceItemIds={Array.from(selectedIds)}
        />
      )}

      {/* Source item detail drawer */}
      <SourceItemDrawer
        open={!!selectedItemId}
        sourceItemId={selectedItemId}
        onClose={() => setSelectedItemId(null)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity} action={snackbar.action} sx={{width: '100%'}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
